import React, {FC, ReactNode} from 'react'
import './ProcurementDocumentSections.css'

interface DocBlockProps {
  title: string
  children: ReactNode
  right?: ReactNode
}

export const DocBlock: FC<DocBlockProps> = ({title, children, right}) => {
  return (
    <div className='border rounded mb-4'>
      <div className='px-4 py-3 border-bottom bg-light d-flex justify-content-between align-items-center'>
        <h6 className='mb-0 fw-bold text-uppercase text-muted' style={{letterSpacing: '0.4px'}}>
          {title}
        </h6>
        {right}
      </div>
      <div className='p-4'>{children}</div>
    </div>
  )
}

interface DocFieldGridProps {
  columns?: 2 | 3 | 4
  children: ReactNode
}

export const DocFieldGrid: FC<DocFieldGridProps> = ({columns = 3, children}) => {
  const colClass =
    columns === 4 ? 'col-lg-3 col-md-6' : columns === 2 ? 'col-md-6' : 'col-lg-4 col-md-6'
  return (
    <div className='row g-3'>
      {React.Children.toArray(children).map((child, idx) => {
        if (child === null || child === undefined) return null
        if (
          React.isValidElement(child) &&
          child.type === DocField &&
          !hasDisplayValue((child.props as DocFieldProps).value)
        ) {
          return null
        }
        return (
          <div key={idx} className={colClass}>
            {child}
          </div>
        )
      })}
    </div>
  )
}

interface DocFieldProps {
  label: string
  value?: ReactNode
}

export const hasDisplayValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') {
    const normalized = value.trim()
    return normalized !== '' && normalized !== '-'
  }
  if (Array.isArray(value)) return value.length > 0
  return true
}

export const DocField: FC<DocFieldProps> = ({label, value}) => {
  if (!hasDisplayValue(value)) return null
  return (
    <div>
      <div className='text-muted fw-semibold fs-8 mb-1 text-uppercase'>{label}</div>
      <div className='text-dark fw-semibold fs-6'>{value}</div>
    </div>
  )
}

interface DocNoteProps {
  title: string
  value?: ReactNode
  children?: ReactNode
}

export const DocNote: FC<DocNoteProps> = ({title, value, children}) => {
  const content = children ?? value
  if (content === null || content === undefined || (typeof content === 'string' && content.trim() === '')) {
    return null
  }
  return (
    <div className='border rounded p-3 h-100'>
      <div className='text-muted fw-semibold fs-8 text-uppercase mb-2'>{title}</div>
      <div
        className='text-dark fs-6'
        style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere'}}
      >
        {content}
      </div>
    </div>
  )
}
